import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import {
  StackedLayout,
  Navbar,
  NavbarSection,
  NavbarItem,
  NavbarSpacer,
  Sidebar,
  SidebarBody,
  SidebarSection,
  SidebarItem,
  Footer,
} from '@/components/layout';
import {
  HomePage,
  DonatePage,
  DonateAmountsPage,
  PledgePage,
  PayForServicePage,
  CheckoutPage,
  SuccessPage,
  CancelPage,
  CallbackPage,
  ProfilePage,
} from '@/pages';
import {
  HomeIcon,
  HeartIcon,
  CreditCardIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ArrowRightStartOnRectangleIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline';
import ConnectLogo from '@/assets/images/Connectlogo.ico';
import ConnectMMC from '@/assets/images/MasjidConnectLogo.png';

// Navigation items configuration
const navItems = [
  { label: 'Home', path: '/', icon: HomeIcon },
  { label: 'Donate', path: '/donate', icon: HeartIcon },
  { label: 'Pledge', path: '/pledge', icon: HandRaisedIcon },
  { label: 'Pay for Service', path: '/pay-for-service', icon: CreditCardIcon },
];

const showMMCBranding = false;

function App() {
  const { companyId, isValid, isLoadingCompany, companyError } = useCompany();
  const { isAuthenticated, user, loginWithRedirect, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogin = () => {
    loginWithRedirect();
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    setIsDropdownOpen(false);
  };

  // Check for login parameter and trigger login
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('login') === 'true' && !isAuthenticated) {
      // Remove the parameter and trigger login
      searchParams.delete('login');
      const newUrl = `${window.location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      loginWithRedirect();
    }
  }, [isAuthenticated, loginWithRedirect]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (isLoadingCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  if (companyError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-2/3">
          <ErrorMessage error={companyError} />
        </div>
      </div>
    );
  }

  if (!isValid || !companyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-2/3 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Company ID Required</h1>
          <p className="text-gray-600 mb-4">
            Please provide a valid company ID in the URL. The ID will be saved for future visits.
          </p>
          <p className="text-gray-600 mb-6">Example:</p>
          <code className="block bg-gray-100 p-4 rounded-lg text-sm text-left overflow-x-auto">
            ?companyId=123
          </code>
        </div>
      </div>
    );
  }

  return (
    <StackedLayout
      navbar={
        <Navbar>
          {showMMCBranding && (
            <NavbarSection>
              <div className="flex items-center gap-2">
                <img src={ConnectLogo} alt="My Masjid Connect" className="h-8 w-8" />
                <span className="text-md text-white">My Masjid Connect | </span>
              </div>
            </NavbarSection>
          )}
          <NavbarSection className="max-lg:hidden">
            {navItems.map(({ label, path, icon: Icon }) => (
              <NavbarItem key={path} href={path}>
                <Icon className="h-5 w-5" />
                {label}
              </NavbarItem>
            ))}
          </NavbarSection>
          <NavbarSpacer />
          <NavbarSection>
            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none"
                  aria-label="User menu"
                >
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name || 'User'}
                      className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <UserCircleIcon className="w-10 h-10 text-white" />
                  )}
                  {user?.name && (
                    <span className="text-white text-sm font-medium hidden md:block">
                      {user.name}
                    </span>
                  )}
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200">
                    <a
                      href="/profile"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <UserCircleIcon className="h-5 w-5 text-gray-500" />
                      <span>Profile</span>
                    </a>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors text-left"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-500" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-primary-300 hover:bg-primary-800 hover:text-white dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-white"
              >
                <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                Login
              </button>
            )}
          </NavbarSection>
          <NavbarSection>
            <div className="flex items-center gap-2 p-4">
              <img src={ConnectMMC} alt="My Masjid Connect" className="object-contain w-25" />
            </div>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarBody>
            <SidebarSection>
              {navItems.map(({ label, path, icon: Icon }) => (
                <SidebarItem key={path} href={path}>
                  <Icon className="h-6 w-6" aria-label={label} />
                </SidebarItem>
              ))}
            </SidebarSection>
          </SidebarBody>
        </Sidebar>
      }
      footer={<Footer />}
    >
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/donate" element={<DonateAmountsPage />} />
        <Route path="/donate-types" element={<DonatePage />} />
        <Route path="/pledge" element={<PledgePage />} />
        <Route path="/pay-for-service" element={<PayForServicePage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/cancel" element={<CancelPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* Default redirect - when not authenticated, go to donate page */}
        <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/donate'} replace />} />
      </Routes>
    </StackedLayout>
  );
}

export default App;
